import { useEffect, useRef, useState } from 'react';

const products = [
  {
    name: 'FOCUS VPN',
    tag: 'Security & Privacy',
    description:
      'A lightning-fast VPN for iOS that encrypts your internet traffic, masks your identity, and keeps your data safe — whether you\'re on public Wi-Fi or at home. Simple to use, impossible to compromise.',
    highlights: ['Military-grade AES-256 encryption', 'Zero-log privacy policy', 'One-tap secure connection', 'Global server coverage'],
    image:
      'https://public.readdy.ai/ai/img_res/b2bf255371db2c2df273593c61e2515a.jpg',
    icon: 'ri-shield-keyhole-line',
    color: 'from-emerald-900 to-gray-900',
  },
  {
    name: 'FOCUS Cleaner',
    tag: 'Performance & Storage',
    description:
      'Reclaim your iPhone\'s true potential. FOCUS Cleaner intelligently scans and removes junk files, duplicate photos, and redundant cache data — delivering a noticeably faster, more responsive device.',
    highlights: ['Smart junk file detection', 'Duplicate photo remover', 'Battery health insights', 'Storage visualizer'],
    image:
      'https://readdy.ai/api/search-image?query=clean%20iOS%20cleaner%20app%20interface%20on%20iPhone%20mockup%20showing%20storage%20optimization%20graphs%20circular%20progress%20bars%20green%20and%20white%20minimal%20UI%20performance%20dashboard%20modern%20app%20design%20professional%20product%20photography%20dark%20background&width=600&height=750&seq=product-cleaner-001&orientation=portrait',
    icon: 'ri-speed-up-line',
    color: 'from-gray-900 to-emerald-950',
  },
];

export default function ProductsSection() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="products" className="py-32 bg-gray-950" ref={ref}>
      <div className="max-w-7xl mx-auto px-8">
        {/* Header */}
        <div
          className={`mb-20 flex flex-col md:flex-row md:items-end justify-between gap-6 transition-all duration-700 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div>
            <span className="inline-block border border-emerald-500/40 text-emerald-400 text-xs font-semibold uppercase tracking-widest px-4 py-2 rounded-full mb-5">
              Our Products
            </span>
            <h2 className="font-syne font-bold text-5xl text-white leading-tight">
              Built for iOS.
              <br />
              <span className="text-emerald-400">Designed for You.</span>
            </h2>
          </div>
          <p className="text-white/40 text-base max-w-xs leading-relaxed md:text-right">
            Two flagship applications engineered to protect your privacy and supercharge your device performance.
          </p>
        </div>

        {/* Product Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {products.map((product, i) => (
            <div
              key={i}
              className={`rounded-3xl overflow-hidden bg-gradient-to-br ${product.color} border border-white/5 transition-all duration-700 hover:-translate-y-1 ${
                visible
                  ? `opacity-100 translate-y-0 delay-[${i * 150}ms]`
                  : 'opacity-0 translate-y-10'
              }`}
            >
              {/* Image */}
              <div className="relative h-72 w-full overflow-hidden">
                <img
                  src={product.image}
                  alt={product.name}
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/30 to-transparent" />
                {/* Tag */}
                <div className="absolute top-5 left-5">
                  <span className="bg-emerald-500/20 border border-emerald-400/30 text-emerald-300 text-xs font-semibold px-3 py-1.5 rounded-full tracking-wide">
                    {product.tag}
                  </span>
                </div>
              </div>

              {/* Content */}
              <div className="p-8">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-emerald-500/15">
                    <i className={`${product.icon} text-emerald-400 text-xl`} />
                  </div>
                  <h3 className="font-syne font-bold text-2xl text-white">{product.name}</h3>
                </div>

                <p className="text-white/50 text-sm leading-relaxed mb-6">{product.description}</p>

                {/* Highlights */}
                <ul className="flex flex-col gap-2">
                  {product.highlights.map((item, j) => (
                    <li key={j} className="flex items-center gap-3 text-white/70 text-sm">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 shrink-0 block" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
